import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'zh';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.dashboard': 'Dashboard',
    'nav.profile': 'Profile',
    'nav.contact': 'Contact',
    'nav.sdk': 'SDK',
    'nav.marketplace': 'Marketplace',
    'nav.login': 'Login',
    'nav.register': 'Register',
    'nav.logout': 'Logout',
    
    // Footer
    'footer.platform': 'Platform',
    'footer.support': 'Support',
    'footer.socialMedia': 'Social Media',
    'footer.helpCenter': 'Help Center',
    'footer.community': 'Community',
    'footer.contactUs': 'Contact Us',
    'footer.status': 'Status',
    'footer.documentation': 'Documentation',
    'footer.apiReference': 'API Reference',
    'footer.copyright': '©2025.07 today Craft.Jamerly.AI. All rights reserved.',
    
    // Home page
    'home.title': 'Enterprise AI Upgrade and Efficiency Enhancement Platform',
    'home.subtitle': 'Discover and deploy enterprise-level AI tools to improve operational efficiency and achieve digital transformation. Join our growing ecosystem of enterprise AI solutions.',
    'home.description': 'Build the future of AI-powered tools with our comprehensive development environment.',
    'home.getStarted': 'Get Started',
    'home.learnMore': 'Learn More',
    'home.whyChoose': 'Why Choose Our Enterprise AI Platform?',
    'home.everythingYouNeed': 'Comprehensive AI upgrade solutions for enterprises',
    'home.easyCreation': 'Quick Deployment',
    'home.easyCreationDesc': 'Simple and easy-to-use AI tool deployment and management platform',
    'home.fastTesting': 'Intelligent Analytics',
    'home.fastTestingDesc': 'Real-time data analysis and business insights for rapid market response',
    'home.globalPublishing': 'Ecosystem Integration',
    'home.globalPublishingDesc': 'Seamless integration with existing enterprise systems to build a complete AI ecosystem',
    'home.secureReliable': 'Secure & Reliable',
    'home.secureReliableDesc': 'Enterprise-grade security to ensure data and business continuity',
    'home.componentsCreated': 'AI Tools Deployed',
    'home.activeDevelopers': 'Active Companies',
    'home.downloads': 'Companies Served',
    'home.readyToStart': 'Ready to Start AI Upgrade?',
    'home.joinThousands': 'Join thousands of companies successfully implementing digital transformation',
    'home.startCreatingNow': 'Start Deploying Now',
    'home.marketplaceTitle': 'Featured AI Tools',
    'home.marketplaceSubtitle': 'Discover powerful tools to enhance your workflow',
    'home.viewAllTools': 'View All Tools',
    'home.buildYourOwnAI': 'Build your own AI',
    'home.promptSubtitle': 'A few simple steps to enjoy AI creation',
    'home.promptPlaceholder': 'Describe the AI application you want to create...',
    'home.promptExample': 'Example: "Create an AI assistant that can answer programming questions"',
    'home.promptShortcut': 'Press Ctrl+Enter to submit quickly',
    'home.startCreating': 'Start Creating',
    'home.exploreMarketplace': 'Explore Marketplace',
    'home.aiTemplatesTitle': 'Choose AI Type Templates',
    'home.aiTemplatesSubtitle': 'Choose from various AI application types to quickly start your AI creation journey',
    'home.conversationType': 'Conversation Type',
    'home.conversationDesc': 'Create intelligent chatbots with multi-turn conversations and context understanding',
    'home.conversationExamples': 'Customer Service Assistant, Education Tutor, Psychological Counselor',
    'home.fileConversionType': 'File Conversion Type',
    'home.fileConversionDesc': 'Intelligent file processing tools supporting format conversion and content extraction',
    'home.fileConversionExamples': 'PDF to Word, Image OCR, Speech to Text',
    'home.imageGenerationType': 'Image Generation Type',
    'home.imageGenerationDesc': 'AI image creation tools that generate high-quality images from descriptions',
    'home.imageGenerationExamples': 'Product Design, Marketing Posters, Illustrations',
    'home.codeAssistantType': 'Code Assistant Type',
    'home.codeAssistantDesc': 'Intelligent programming assistant for code generation, debugging and optimization',
    'home.codeAssistantExamples': 'Code Generator, Bug Fixer, Code Reviewer',
    'home.trendsTitle': 'Hot AI Application Trends',
    'home.trendsSubtitle': 'Discover popular AI applications created by users',
    'home.viewMoreApps': 'View More Popular Apps',
    'home.createdBy': 'by',
    'home.created': 'created',
    'home.type': 'Type',
    'home.usageCount': 'Usage Count',
    'home.growthTrend': 'Growth Trend',
    'home.platformName': 'Craft.Jamerly.AI Platform',
    'home.appExamples': 'App Examples:',
    'home.appsCount': 'apps',
    
    // Popular apps data
    'apps.smartCustomerService.name': 'Smart Customer Service Pro',
    'apps.smartCustomerService.creator': 'AI Studio',
    'apps.smartCustomerService.category': 'Conversation Type',
    'apps.smartCustomerService.description': '24/7 intelligent customer service with multilingual support and automatic FAQ handling',
    
    'apps.documentAnalysis.name': 'Document Intelligence Expert',
    'apps.documentAnalysis.creator': 'Data Tech',
    'apps.documentAnalysis.category': 'File Conversion Type',
    'apps.documentAnalysis.description': 'Intelligent parsing of PDF, Word, Excel files with automatic key information extraction',
    
    'apps.aiDesigner.name': 'AI Designer Studio',
    'apps.aiDesigner.creator': 'Creative Unlimited',
    'apps.aiDesigner.category': 'Image Generation Type',
    'apps.aiDesigner.description': 'Professional AI design tool supporting posters, logos, and UI design',
    
    'apps.codeOptimizer.name': 'Code Optimization Master',
    'apps.codeOptimizer.creator': 'Programming Lab',
    'apps.codeOptimizer.category': 'Code Assistant Type',
    'apps.codeOptimizer.description': 'Intelligent code review, performance optimization, and automatic bug fixing',
    
    'apps.multilingualTranslator.name': 'Multilingual Translation Expert',
    'apps.multilingualTranslator.creator': 'Language Tech',
    'apps.multilingualTranslator.category': 'Conversation Type',
    'apps.multilingualTranslator.description': 'Real-time translation for 100+ languages with accurate technical terminology',
    
    'apps.financialAnalysis.name': 'Financial Analysis Assistant',
    'apps.financialAnalysis.creator': 'Financial Intelligence',
    'apps.financialAnalysis.category': 'Conversation Type',
    'apps.financialAnalysis.description': 'Financial report analysis, investment advice, and risk assessment',
    
    'apps.videoGenerator.name': 'Video Content Generator',
    'apps.videoGenerator.creator': 'Media Tech',
    'apps.videoGenerator.category': 'Image Generation Type',
    'apps.videoGenerator.description': 'AI video production with automatic editing and special effects generation',
    
    'apps.legalAssistant.name': 'Legal Document Assistant',
    'apps.legalAssistant.creator': 'Legal AI',
    'apps.legalAssistant.category': 'File Conversion Type',
    'apps.legalAssistant.description': 'Contract review, legal document generation, and risk assessment',
    
    'apps.medicalDiagnosis.name': 'Medical Diagnosis Assistant',
    'apps.medicalDiagnosis.creator': 'Medical Tech',
    'apps.medicalDiagnosis.category': 'Conversation Type',
    'apps.medicalDiagnosis.description': 'Symptom analysis, preliminary diagnosis, and health advice',
    
    'apps.educationTutor.name': 'Education Tutoring Teacher',
    'apps.educationTutor.creator': 'Smart Education',
    'apps.educationTutor.category': 'Conversation Type',
    'apps.educationTutor.description': 'Personalized learning plans, problem-solving, and knowledge Q&A',
    
    'apps.marketingCopy.name': 'Marketing Copy Generator',
    'apps.marketingCopy.creator': 'Marketing AI',
    'apps.marketingCopy.category': 'Conversation Type',
    'apps.marketingCopy.description': 'Ad copy, product descriptions, and marketing strategies',
    
    'apps.3dModelGenerator.name': '3D Model Generator',
    'apps.3dModelGenerator.creator': '3D Tech',
    'apps.3dModelGenerator.category': 'Image Generation Type',
    'apps.3dModelGenerator.description': 'AI-generated 3D models with support for multiple export formats',
    
    // Contact page
    'contact.badge': 'Contact Us',
    'contact.title': 'Get in Touch',
    'contact.subtitle': 'We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.',
    'contact.formTitle': 'Send us a Message',
    'contact.formDescription': 'Fill out the form below and we\'ll get back to you within 24 hours.',
    'contact.name': 'Name',
    'contact.namePlaceholder': 'Enter your full name',
    'contact.email': 'Email',
    'contact.emailPlaceholder': 'Enter your email address',
    'contact.subject': 'Subject',
    'contact.subjectPlaceholder': 'What is this about?',
    'contact.message': 'Message',
    'contact.messagePlaceholder': 'Tell us more about your inquiry...',
    'contact.sendMessage': 'Send Message',
    'contact.sending': 'Sending...',
    'contact.successMessage': 'Thank you! Your message has been sent successfully.',
    'contact.infoTitle': 'Contact Information',
    'contact.infoDescription': 'Get in touch with us through any of the following channels.',
    'contact.emailDesc': 'Send us an email anytime',
    'contact.phone': 'Phone',
    'contact.phoneDesc': 'Call us during business hours',
    'contact.address': 'Address',
    'contact.addressValue': 'Singapore',
    'contact.addressDesc': 'Visit our office',
    'contact.businessHours': 'Business Hours',
    'contact.businessHoursValue': 'Mon-Fri 9:00 AM - 6:00 PM',
    'contact.businessHoursDesc': 'We\'re here to help',
    'contact.companyName': 'Craft.Jamerly.AI',
    'contact.companyDesc': 'Simple AI, Simple create',
    'contact.website': 'Website',
    
    // Profile page
    'profile.title': 'Profile',
    'profile.subtitle': 'View and manage your account information',
    'profile.basicInfo': 'Basic Information',
    'profile.basicInfoDesc': 'Your account basic information',
    'profile.username': 'Username',
    'profile.nickname': 'Nickname',
    'profile.realName': 'Real Name',
    'profile.gender': 'Gender',
    'profile.language': 'Language',
    'profile.notSet': 'Not Set',
    'profile.accountStatus': 'Account Status',
    'profile.usernameFrozen': 'Username Frozen',
    'profile.accountLocked': 'Account Locked',
    'profile.accountBlocked': 'Account Blocked',
    'profile.contactInfo': 'Contact Information',
    'profile.contactInfoDesc': 'Your contact information',
    'profile.email': 'Email Address',
    'profile.phone': 'Phone Number',
    'profile.timeInfo': 'Time Information',
    'profile.timeInfoDesc': 'Account time-related information',
    'profile.registrationTime': 'Registration Time',
    'profile.lastModified': 'Last Modified',
    'profile.birthday': 'Birthday',
    'profile.backToDashboard': 'Back to Dashboard',
    'profile.editProfile': 'Edit Profile',
    'profile.gender.male': 'Male',
    'profile.gender.female': 'Female',
    'profile.gender.unknown': 'Unknown',
    'profile.status.normal': 'Normal',
    'profile.status.blocked': 'Blocked',
    'profile.status.locked': 'Locked',
    'profile.yes': 'Yes',
    'profile.no': 'No',
    
    // Language switcher
    'language.en': 'English',
    'language.zh': '中文',
    'language.switch': 'Switch Language',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.submit': 'Submit',
    'common.reset': 'Reset',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.refresh': 'Refresh',
    'common.close': 'Close',
    'common.open': 'Open',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.confirm': 'Confirm',
    'common.dismiss': 'Dismiss',
    
    // Login page
    'login.title': 'Sign in to your account',
    'login.description': 'Enter your email and password to access your account',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.signIn': 'Sign in',
    'login.signingIn': 'Signing in...',
    'login.noAccount': "Don't have an account?",
    'login.signUp': 'Sign up',
    'login.success': 'Login successful!',
    'login.failed': 'Login failed',
    'login.username': 'Username',
    'login.usernamePlaceholder': 'Enter your username',
    'login.passwordPlaceholder': 'Enter your password',
    'login.welcome': 'Welcome back to Craft.Jamerly.AI',
    'login.enterUsername': 'Please enter username',
    'login.enterPassword': 'Please enter password',
    'login.loginFailed': 'Login failed, please check username and password',
    'login.loginError': 'Login failed, please try again later',
    'login.registerNow': 'Register now',
    
    // Register page
    'register.title': 'Create Account',
    'register.description': 'Join the Craft.Jamerly.AI community',
    'register.username': 'Username *',
    'register.usernamePlaceholder': 'Enter username (3-20 characters)',
    'register.usernameHint': 'Only letters, numbers and underscores allowed',
    'register.password': 'Password *',
    'register.passwordPlaceholder': 'Enter password (at least 6 characters)',
    'register.confirmPassword': 'Confirm Password *',
    'register.confirmPasswordPlaceholder': 'Enter password again',
    'register.createAccount': 'Create Account',
    'register.creating': 'Creating account...',
    'register.success': 'Registration successful! Please log in to your account.',
    'register.failed': 'Registration failed, please try again later',
    'register.haveAccount': 'Already have an account?',
    'register.signIn': 'Sign in now',
    
    // Validation messages
    'validation.usernameTooShort': 'Username must be at least 3 characters',
    'validation.usernameTooLong': 'Username cannot exceed 20 characters',
    'validation.usernameInvalid': 'Username can only contain letters, numbers and underscores',
    'validation.passwordTooShort': 'Password must be at least 6 characters',
    'validation.passwordTooLong': 'Password cannot exceed 50 characters',
    'validation.passwordMismatch': 'Passwords do not match',
    
    // Agent page
    'agent.title': 'AI Agent Conversation',
    'agent.subtitle': 'Chat with AI assistant and create intelligent agents',
    'agent.chatTab': 'Chat Module',
    'agent.editorTab': 'Agent Editor',
    'agent.chatTitle': 'AI Chat',
    'agent.chatDescription': 'Chat with AI assistant and describe your needs',
    'agent.inputPlaceholder': 'Enter your message...',
    'agent.quickActions': 'Quick Actions',
    'agent.quickActionsDesc': 'Quick access to common functions',
    'agent.viewAgentStatus': 'View Agent Status',
    'agent.saveChat': 'Save Chat',
    'agent.copyChat': 'Copy Chat',
    'agent.exportChat': 'Export Chat',
    'agent.editorTitle': 'Agent Editor',
    'agent.editorDescription': 'View and edit agent processing flow',
    'agent.intentRecognition': 'Intent Recognition',
    'agent.intentDescription': 'Analyze user intent and requirements',
    'agent.dataIntegration': 'Data Integration',
    'agent.dataDescription': 'Collect and integrate relevant data',
    'agent.executionTools': 'Execution Tools',
    'agent.toolsDescription': 'Select appropriate tools to execute tasks',
    'agent.stepContent': 'Step content...',
    'agent.backToChat': 'Back to Chat',
    'agent.executeAgent': 'Execute Agent',
    'agent.models': 'AI Models',
    'agent.modelsDesc': 'Available AI models for your conversations',
    'agent.modelProvider': 'Provider',
    'agent.modelVersion': 'Version',
    'agent.modelCapabilities': 'Capabilities',
    'agent.modelMaxTokens': 'Max Tokens',
    'agent.modelActive': 'Active',
    'agent.modelInactive': 'Inactive',
    'agent.refreshModels': 'Refresh Models',
    'agent.loadingModels': 'Loading models...',
    'agent.noModels': 'No models available',
    'agent.selectModel': 'Select Model',
    
    // Dashboard page
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',
    'dashboard.overview': 'Overview',
    'dashboard.overviewDesc': 'Your AI agents and platform usage',
    'dashboard.stats': 'Statistics',
    'dashboard.totalAgents': 'Total Agents',
    'dashboard.activeAgents': 'Active Agents',
    'dashboard.totalUsage': 'Total Usage',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.recentActivityDesc': 'Latest activities from your agents',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.quickActionsDesc': 'Common tasks and shortcuts',
    'dashboard.createAgent': 'Create Agent',
    'dashboard.viewAll': 'View All',
    'dashboard.noActivity': 'No recent activity',
    'dashboard.noActivityDesc': 'Your agent activities will appear here',
    
    // Agent List
    'agent.myAgents': 'My Agents',
    'agent.manageAgents': 'Manage and monitor your AI agents',
    'agent.createNew': 'Create New Agent',
    'agent.createFirst': 'Create Your First Agent',
    'agent.noAgents': 'No Agents Yet',
    'agent.noAgentsDesc': 'Create your first AI agent to get started',
    'agent.chat': 'Chat',
    'agent.edit': 'Edit',
    'agent.delete': 'Delete',
    'agent.activate': 'Activate',
    'agent.deactivate': 'Deactivate',
    'agent.usageCount': 'Usage Count',
    'agent.lastUsed': 'Last Used',
    'agent.status.active': 'Active',
    'agent.status.inactive': 'Inactive',
    'agent.status.draft': 'Draft',
    'agent.status.error': 'Error',
    'agent.type.conversation': 'Conversation',
    'agent.type.fileConversion': 'File Conversion',
    'agent.type.imageGeneration': 'Image Generation',
    'agent.type.codeAssistant': 'Code Assistant',
    
    // Agent Detail Page
    'agentDetail.backToDashboard': 'Back to Dashboard',
    'agentDetail.createNewAgent': 'Create New Agent',
    'agentDetail.agentDetails': 'Agent Details',
    'agentDetail.configureNewAgent': 'Configure your new AI agent',
    'agentDetail.editAgentConfig': 'Edit agent configuration and settings',
    'agentDetail.save': 'Save',
    'agentDetail.saving': 'Saving...',
    'agentDetail.deactivate': 'Deactivate',
    'agentDetail.activate': 'Activate',
    'agentDetail.delete': 'Delete',
    'agentDetail.deleteConfirm': 'Are you sure you want to delete "{name}"?',
    'agentDetail.created': 'Created',
    'agentDetail.uses': 'uses',
    'agentDetail.lastUsed': 'Last used',
    'agentDetail.basicInfo': 'Basic Info',
    'agentDetail.modelPrompt': 'Model & Prompt',
    'agentDetail.advanced': 'Advanced',
    'agentDetail.preview': 'Preview',
    'agentDetail.basicInformation': 'Basic Information',
    'agentDetail.basicInfoDesc': 'Configure the basic details of your AI agent',
    'agentDetail.name': 'Name',
    'agentDetail.namePlaceholder': 'Enter agent name',
    'agentDetail.type': 'Type',
    'agentDetail.selectAgentType': 'Select agent type',
    'agentDetail.description': 'Description',
    'agentDetail.descriptionPlaceholder': 'Describe what this agent does...',
    'agentDetail.tags': 'Tags',
    'agentDetail.addTag': 'Add a tag',
    'agentDetail.add': 'Add',
    'agentDetail.modelConfiguration': 'Model Configuration',
    'agentDetail.modelConfigDesc': 'Select the AI model and configure the prompt for your agent',
    'agentDetail.aiModel': 'AI Model',
    'agentDetail.selectModel': 'Select an AI model',
    'agentDetail.systemPrompt': 'System Prompt',
    'agentDetail.promptPlaceholder': 'Enter the system prompt that defines your agent\'s behavior...',
    'agentDetail.promptHelp': 'This prompt will be used to instruct the AI model on how to behave and respond.',
    'agentDetail.advancedSettings': 'Advanced Settings',
    'agentDetail.advancedDesc': 'Configure advanced options for your agent',
    'agentDetail.status': 'Status',
    'agentDetail.selectStatus': 'Select status',
    'agentDetail.configOptions': 'Configuration Options',
    'agentDetail.configOptionsDesc': 'Advanced configuration options will be available here in future updates.',
    'agentDetail.agentPreview': 'Agent Preview',
    'agentDetail.previewDesc': 'Preview how your agent will appear to users',
    'agentDetail.agentName': 'Agent Name',
    'agentDetail.agentDescription': 'Agent description',
    'agentDetail.model': 'Model',
    'agentDetail.fillRequiredFields': 'Please fill in all required fields',
    'agentDetail.agentCreated': 'Agent created successfully',
    'agentDetail.agentUpdated': 'Agent updated successfully',
    'agentDetail.agentDeleted': 'Agent deleted successfully',
    'agentDetail.agentNotFound': 'Agent not found',
    'agentDetail.failedLoadData': 'Failed to load agent data',
    'agentDetail.failedSave': 'Failed to save agent',
    'agentDetail.failedDelete': 'Failed to delete agent',
    'agentDetail.failedUpdateStatus': 'Failed to update agent status',
    'agentDetail.agentActivated': 'Agent activated',
    'agentDetail.agentDeactivated': 'Agent deactivated',
    
    // Marketplace
    'marketplace.title': 'AI Tools Marketplace',
    'marketplace.subtitle': 'Discover and explore powerful AI tools to enhance your workflow',
    'marketplace.searchPlaceholder': 'Search AI tools...',
    'marketplace.featured': 'Featured',
    'marketplace.by': 'by',
    'marketplace.viewDetails': 'View Details',
    'marketplace.noResults': 'No tools found',
    'marketplace.noResultsDesc': 'Try adjusting your search or filter criteria',
    'marketplace.backToMarketplace': 'Back to Marketplace',
    'marketplace.toolNotFound': 'Tool not found',
    'marketplace.getStarted': 'Get Started',
    'marketplace.favorite': 'Favorite',
    'marketplace.share': 'Share',
    'marketplace.reviews': 'reviews',
    'marketplace.mostPopular': 'Most Popular',
    'marketplace.choosePlan': 'Choose Plan',
    'marketplace.installation': 'Installation',
    'marketplace.setup': 'Setup',
    'marketplace.usage': 'Usage',
    'marketplace.visitWebsite': 'Visit Website',
    'marketplace.contact': 'Contact',
    'marketplace.reply': 'Reply',
    'marketplace.overview': 'Overview',
    'marketplace.requirements': 'System Requirements',
    'marketplace.os': 'Operating System',
    'marketplace.memory': 'Memory',
    'marketplace.storage': 'Storage',
    'marketplace.internet': 'Internet',
    'marketplace.details': 'Tool Details',
    'marketplace.version': 'Version',
    'marketplace.lastUpdated': 'Last Updated',
    'marketplace.category': 'Category',
    'marketplace.developer': 'Developer',
    'marketplace.sort.featured': 'Featured',
    'marketplace.sort.rating': 'Highest Rated',
    'marketplace.sort.priceLow': 'Price: Low to High',
    'marketplace.sort.priceHigh': 'Price: High to Low',
    'marketplace.sort.reviews': 'Most Reviews',
    'marketplace.tabs.overview': 'Overview',
    'marketplace.tabs.pricing': 'Pricing',
    'marketplace.tabs.instructions': 'Instructions',
    'marketplace.tabs.developer': 'Developer',
    'marketplace.tabs.reviews': 'Reviews',
    
    // Tool names and categories
    'tools.smartCustomerService': 'Smart Customer Service Assistant',
    'tools.financialAnalysis': 'Financial Intelligence Analysis',
    'tools.hrManagement': 'HR Intelligence Management',
    'tools.supplyChain': 'Supply Chain Optimization System',
    'tools.salesAssistant': 'Sales Intelligence Assistant',
    'tools.marketAnalysis': 'Market Intelligence Analysis',
    'tools.productionOptimization': 'Production Process Optimization',
    'tools.complianceMonitoring': 'Compliance Intelligence Monitoring',
    'tools.decisionPlatform': 'Decision Intelligence Platform',
    'tools.projectManagement': 'Project Management Intelligence Assistant',
    'tools.knowledgeManagement': 'Knowledge Management Intelligence System',
    'tools.trainingPlatform': 'Training Intelligence Platform',
    'tools.marketingAutomation': 'Intelligent Marketing Automation',
    
    'category.customerService': 'Customer Service',
    'category.financialManagement': 'Financial Management',
    'category.humanResources': 'Human Resources',
    'category.supplyChain': 'Supply Chain',
    'category.salesManagement': 'Sales Management',
    'category.marketAnalysis': 'Market Analysis',
    'category.productionManagement': 'Production Management',
    'category.complianceManagement': 'Compliance Management',
    'category.decisionSupport': 'Decision Support',
    'category.projectManagement': 'Project Management',
    'category.knowledgeManagement': 'Knowledge Management',
    'category.trainingDevelopment': 'Training & Development',
    'category.marketingAutomation': 'Marketing Automation',
    
    // Tool descriptions
    'tools.smartCustomerService.desc': '24/7 intelligent customer service system, automatically handling customer inquiries and improving satisfaction',
    'tools.financialAnalysis.desc': 'AI-driven financial data analysis tool, automatically generating financial reports and predictive analysis',
    'tools.hrManagement.desc': 'Full-process HR management platform, AI-powered solution from recruitment to performance evaluation',
    'tools.supplyChain.desc': 'Intelligent supply chain management, real-time inventory monitoring, optimizing logistics efficiency',
    'tools.salesAssistant.desc': 'AI sales assistant, intelligent lead management, improving sales conversion and customer relationships',
    'tools.marketAnalysis.desc': 'Real-time market monitoring and analysis, competitor tracking, market trend prediction',
    'tools.productionOptimization.desc': 'Smart manufacturing solution, improving production efficiency and reducing operational costs',
    'tools.complianceMonitoring.desc': 'Automated compliance checking, risk warning, ensuring enterprise compliance operations',
    'tools.decisionPlatform.desc': 'Big data-based intelligent decision support system, providing business insights and recommendations',
    'tools.projectManagement.desc': 'AI-driven project management tool, automatic task assignment, progress monitoring and resource optimization',
    'tools.knowledgeManagement.desc': 'Enterprise knowledge base intelligent management, automatic document classification, knowledge retrieval and sharing',
    'tools.trainingPlatform.desc': 'Personalized employee training system, AI course recommendations, learning effectiveness evaluation',
    'tools.marketingAutomation.desc': 'Omni-channel intelligent marketing automation platform, precise customer reach, improving conversion rates',
    
    // Tool tags
    'tools.tags.customerAutomation': 'Customer Service Automation',
    'tools.tags.multilingual': 'Multilingual Support',
    'tools.tags.intelligentRouting': 'Intelligent Routing',
    'tools.tags.financialAnalysis': 'Financial Analysis',
    'tools.tags.predictiveModeling': 'Predictive Modeling',
    'tools.tags.riskControl': 'Risk Control',
    'tools.tags.recruitmentManagement': 'Recruitment Management',
    'tools.tags.performanceEvaluation': 'Performance Evaluation',
    'tools.tags.employeeTraining': 'Employee Training',
    'tools.tags.inventoryManagement': 'Inventory Management',
    'tools.tags.logisticsOptimization': 'Logistics Optimization',
    'tools.tags.demandForecasting': 'Demand Forecasting',
    'tools.tags.leadManagement': 'Lead Management',
    'tools.tags.customerRelationships': 'Customer Relationships',
    'tools.tags.salesForecasting': 'Sales Forecasting',
    'tools.tags.marketMonitoring': 'Market Monitoring',
    'tools.tags.competitorAnalysis': 'Competitor Analysis',
    'tools.tags.trendPrediction': 'Trend Prediction',
    'tools.tags.productionOptimization': 'Production Optimization',
    'tools.tags.qualityControl': 'Quality Control',
    'tools.tags.equipmentMaintenance': 'Equipment Maintenance',
    'tools.tags.complianceChecking': 'Compliance Checking',
    'tools.tags.riskWarning': 'Risk Warning',
    'tools.tags.auditTracking': 'Audit Tracking',
    'tools.tags.dataAnalysis': 'Data Analysis',
    'tools.tags.businessIntelligence': 'Business Intelligence',
    'tools.tags.decisionSupport': 'Decision Support',
    'tools.tags.taskManagement': 'Task Management',
    'tools.tags.progressMonitoring': 'Progress Monitoring',
    'tools.tags.resourceOptimization': 'Resource Optimization',
    'tools.tags.knowledgeBase': 'Knowledge Base',
    'tools.tags.documentManagement': 'Document Management',
    'tools.tags.intelligentRetrieval': 'Intelligent Retrieval',
    'tools.tags.onlineTraining': 'Online Training',
    'tools.tags.personalizedLearning': 'Personalized Learning',
    'tools.tags.effectivenessEvaluation': 'Effectiveness Evaluation',
    'tools.tags.marketingAutomation': 'Marketing Automation',
    'tools.tags.customerReach': 'Customer Reach',
    'tools.tags.conversionOptimization': 'Conversion Optimization',
  },
  zh: {
    // Navigation
    'nav.home': '首页',
    'nav.dashboard': '仪表板',
    'nav.profile': '个人资料',
    'nav.contact': '联系我们',
    'nav.sdk': 'SDK',
    'nav.marketplace': '市场',
    'nav.login': '登录',
    'nav.register': '注册',
    'nav.logout': '退出登录',
    
    // Footer
    'footer.platform': '平台',
    'footer.support': '支持',
    'footer.socialMedia': '社交媒体',
    'footer.helpCenter': '帮助中心',
    'footer.community': '社区',
    'footer.contactUs': '联系我们',
    'footer.status': '状态',
    'footer.documentation': '文档',
    'footer.apiReference': 'API 参考',
    'footer.copyright': '©2025.07 today Craft.Jamerly.AI. 保留所有权利。',
    
    // Home page
    'home.title': '企业AI升级与效率提升平台',
    'home.subtitle': '发现和部署企业级AI工具，提升运营效率，实现数字化转型。加入我们不断发展的企业AI解决方案生态。',
    'home.description': '通过我们全面的开发环境构建 AI 驱动工具的未来。',
    'home.getStarted': '开始使用',
    'home.learnMore': '了解更多',
    'home.whyChoose': '为什么选择我们的企业AI平台？',
    'home.everythingYouNeed': '为企业提供全方位的AI升级解决方案',
    'home.easyCreation': '快速部署',
    'home.easyCreationDesc': '简单易用的AI工具部署和管理平台',
    'home.fastTesting': '智能分析',
    'home.fastTestingDesc': '实时数据分析和业务洞察，快速响应市场变化',
    'home.globalPublishing': '生态集成',
    'home.globalPublishingDesc': '与现有企业系统无缝集成，构建完整的AI生态',
    'home.secureReliable': '安全可靠',
    'home.secureReliableDesc': '企业级安全保障，确保数据和业务连续性',
    'home.componentsCreated': 'AI工具部署',
    'home.activeDevelopers': '活跃企业',
    'home.downloads': '服务企业',
    'home.readyToStart': '准备开始AI升级？',
    'home.joinThousands': '加入数千家成功实现数字化转型的企业',
    'home.startCreatingNow': '立即开始部署',
    'home.marketplaceTitle': '精选 AI 工具',
    'home.marketplaceSubtitle': '发现强大的工具来增强您的工作流程',
    'home.viewAllTools': '查看所有工具',
    'home.buildYourOwnAI': '构建你自己的AI',
    'home.promptSubtitle': '简单几步，享AI创作',
    'home.promptPlaceholder': '描述你想要创建的AI应用...',
    'home.promptExample': '例如："创建一个能够回答编程问题的AI助手"',
    'home.promptShortcut': '按 Ctrl+Enter 快速提交',
    'home.startCreating': '开始创建',
    'home.exploreMarketplace': '探索市场',
    'home.aiTemplatesTitle': '选择AI类型模板',
    'home.aiTemplatesSubtitle': '从多种AI应用类型中选择，快速开始你的AI创作之旅',
    'home.conversationType': '对话类型',
    'home.conversationDesc': '创建智能对话机器人，支持多轮对话和上下文理解',
    'home.conversationExamples': '客服助手, 教育导师, 心理咨询师',
    'home.fileConversionType': '文件转换类型',
    'home.fileConversionDesc': '智能文件处理工具，支持格式转换和内容提取',
    'home.fileConversionExamples': 'PDF转Word, 图片文字识别, 语音转文字',
    'home.imageGenerationType': '图像生成类型',
    'home.imageGenerationDesc': 'AI图像创作工具，根据描述生成高质量图片',
    'home.imageGenerationExamples': '产品设计图, 营销海报, 插画创作',
    'home.codeAssistantType': '代码助手类型',
    'home.codeAssistantDesc': '智能编程助手，代码生成、调试和优化',
    'home.codeAssistantExamples': '代码生成器, Bug修复助手, 代码审查',
    'home.trendsTitle': '热门AI应用趋势',
    'home.trendsSubtitle': '发现用户正在创建的热门AI应用',
    'home.viewMoreApps': '查看更多热门应用',
    'home.createdBy': '由',
    'home.created': '创建',
    'home.type': '类型',
    'home.usageCount': '使用次数',
    'home.growthTrend': '增长趋势',
    'home.platformName': 'Craft.Jamerly.AI 平台',
    'home.appExamples': '应用示例：',
    'home.appsCount': '个应用',
    
    // Popular apps data
    'apps.smartCustomerService.name': '智能客服助手 Pro',
    'apps.smartCustomerService.creator': 'AI工作室',
    'apps.smartCustomerService.category': '对话类型',
    'apps.smartCustomerService.description': '24/7智能客服，支持多语言，自动处理常见问题',
    
    'apps.documentAnalysis.name': '文档智能分析专家',
    'apps.documentAnalysis.creator': '数据科技',
    'apps.documentAnalysis.category': '文件转换类型',
    'apps.documentAnalysis.description': 'PDF、Word、Excel智能解析，自动提取关键信息',
    
    'apps.aiDesigner.name': 'AI设计师 Studio',
    'apps.aiDesigner.creator': '创意无限',
    'apps.aiDesigner.category': '图像生成类型',
    'apps.aiDesigner.description': '专业级AI设计工具，支持海报、Logo、UI设计',
    
    'apps.codeOptimizer.name': '代码优化大师',
    'apps.codeOptimizer.creator': '编程实验室',
    'apps.codeOptimizer.category': '代码助手类型',
    'apps.codeOptimizer.description': '智能代码审查，性能优化，Bug自动修复',
    
    'apps.multilingualTranslator.name': '多语言翻译专家',
    'apps.multilingualTranslator.creator': '语言科技',
    'apps.multilingualTranslator.category': '对话类型',
    'apps.multilingualTranslator.description': '支持100+语言实时翻译，专业术语准确翻译',
    
    'apps.financialAnalysis.name': '财务分析助手',
    'apps.financialAnalysis.creator': '金融智能',
    'apps.financialAnalysis.category': '对话类型',
    'apps.financialAnalysis.description': '财务报表分析，投资建议，风险评估',
    
    'apps.videoGenerator.name': '视频内容生成器',
    'apps.videoGenerator.creator': '媒体科技',
    'apps.videoGenerator.category': '图像生成类型',
    'apps.videoGenerator.description': 'AI视频制作，自动剪辑，特效生成',
    
    'apps.legalAssistant.name': '法律文书助手',
    'apps.legalAssistant.creator': '法律AI',
    'apps.legalAssistant.category': '文件转换类型',
    'apps.legalAssistant.description': '合同审查，法律文书生成，风险评估',
    
    'apps.medicalDiagnosis.name': '医疗诊断助手',
    'apps.medicalDiagnosis.creator': '医疗科技',
    'apps.medicalDiagnosis.category': '对话类型',
    'apps.medicalDiagnosis.description': '症状分析，初步诊断，健康建议',
    
    'apps.educationTutor.name': '教育辅导老师',
    'apps.educationTutor.creator': '智慧教育',
    'apps.educationTutor.category': '对话类型',
    'apps.educationTutor.description': '个性化学习计划，题目解析，知识问答',
    
    'apps.marketingCopy.name': '营销文案生成器',
    'apps.marketingCopy.creator': '营销AI',
    'apps.marketingCopy.category': '对话类型',
    'apps.marketingCopy.description': '广告文案，产品描述，营销策略',
    
    'apps.3dModelGenerator.name': '3D模型生成器',
    'apps.3dModelGenerator.creator': '3D科技',
    'apps.3dModelGenerator.category': '图像生成类型',
    'apps.3dModelGenerator.description': 'AI生成3D模型，支持多种格式导出',
    
    // Contact page
    'contact.badge': '联系我们',
    'contact.title': '联系我们',
    'contact.subtitle': '我们很乐意听到您的声音。发送消息给我们，我们会尽快回复。',
    'contact.formTitle': '发送消息',
    'contact.formDescription': '填写下面的表单，我们将在24小时内回复您。',
    'contact.name': '姓名',
    'contact.namePlaceholder': '请输入您的全名',
    'contact.email': '邮箱',
    'contact.emailPlaceholder': '请输入您的邮箱地址',
    'contact.subject': '主题',
    'contact.subjectPlaceholder': '这是关于什么的？',
    'contact.message': '消息',
    'contact.messagePlaceholder': '请详细描述您的咨询内容...',
    'contact.sendMessage': '发送消息',
    'contact.sending': '发送中...',
    'contact.successMessage': '谢谢！您的消息已成功发送。',
    'contact.infoTitle': '联系信息',
    'contact.infoDescription': '通过以下任何渠道与我们联系。',
    'contact.emailDesc': '随时发送邮件给我们',
    'contact.phone': '电话',
    'contact.phoneDesc': '在工作时间内致电我们',
    'contact.address': '地址',
    'contact.addressValue': '新加坡',
    'contact.addressDesc': '访问我们的办公室',
    'contact.businessHours': '营业时间',
    'contact.businessHoursValue': '周一至周五 9:00 - 18:00',
    'contact.businessHoursDesc': '我们随时为您服务',
    'contact.companyName': 'Craft.Jamerly.AI',
    'contact.companyDesc': 'Simple AI, Simple create',
    'contact.website': '网站',
    
    // Profile page
    'profile.title': '个人资料',
    'profile.subtitle': '查看和管理您的账户信息',
    'profile.basicInfo': '基本信息',
    'profile.basicInfoDesc': '您的账户基本信息',
    'profile.username': '用户名',
    'profile.nickname': '昵称',
    'profile.realName': '真实姓名',
    'profile.gender': '性别',
    'profile.language': '语言',
    'profile.notSet': '未设置',
    'profile.accountStatus': '账户状态',
    'profile.usernameFrozen': '用户名冻结',
    'profile.accountLocked': '账户锁定',
    'profile.accountBlocked': '账户封禁',
    'profile.contactInfo': '联系信息',
    'profile.contactInfoDesc': '您的联系方式',
    'profile.email': '邮箱地址',
    'profile.phone': '手机号码',
    'profile.timeInfo': '时间信息',
    'profile.timeInfoDesc': '账户时间相关信息',
    'profile.registrationTime': '注册时间',
    'profile.lastModified': '最后修改',
    'profile.birthday': '生日',
    'profile.backToDashboard': '返回仪表板',
    'profile.editProfile': '编辑资料',
    'profile.gender.male': '男',
    'profile.gender.female': '女',
    'profile.gender.unknown': '未知',
    'profile.status.normal': '正常',
    'profile.status.blocked': '已封禁',
    'profile.status.locked': '已锁定',
    'profile.yes': '是',
    'profile.no': '否',
    
    // Language switcher
    'language.en': 'English',
    'language.zh': '中文',
    'language.switch': '切换语言',
    
    // Common
    'common.loading': '加载中...',
    'common.error': '错误',
    'common.success': '成功',
    'common.cancel': '取消',
    'common.save': '保存',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.view': '查看',
    'common.back': '返回',
    'common.next': '下一步',
    'common.previous': '上一步',
    'common.submit': '提交',
    'common.reset': '重置',
    'common.search': '搜索',
    'common.filter': '筛选',
    'common.sort': '排序',
    'common.refresh': '刷新',
    'common.close': '关闭',
    'common.open': '打开',
    'common.yes': '是',
    'common.no': '否',
    'common.ok': '确定',
    'common.confirm': '确认',
    'common.dismiss': '忽略',
    
    // Login page
    'login.title': '登录您的账户',
    'login.description': '输入您的邮箱和密码以访问您的账户',
    'login.email': '邮箱',
    'login.password': '密码',
    'login.signIn': '登录',
    'login.signingIn': '登录中...',
    'login.noAccount': '还没有账户？',
    'login.signUp': '注册',
    'login.success': '登录成功！',
    'login.failed': '登录失败',
    'login.username': '用户名',
    'login.usernamePlaceholder': '请输入用户名',
    'login.passwordPlaceholder': '请输入密码',
    'login.welcome': '欢迎回到 Craft.Jamerly.AI',
    'login.enterUsername': '请输入用户名',
    'login.enterPassword': '请输入密码',
    'login.loginFailed': '登录失败，请检查用户名和密码',
    'login.loginError': '登录失败，请稍后重试',
    'login.registerNow': '立即注册',
    
    // Register page
    'register.title': '创建账户',
    'register.description': '加入 Craft.Jamerly.AI 社区',
    'register.username': '用户名 *',
    'register.usernamePlaceholder': '请输入用户名 (3-20个字符)',
    'register.usernameHint': '仅允许字母、数字和下划线',
    'register.password': '密码 *',
    'register.passwordPlaceholder': '请输入密码 (至少6个字符)',
    'register.confirmPassword': '确认密码 *',
    'register.confirmPasswordPlaceholder': '请再次输入密码',
    'register.createAccount': '创建账户',
    'register.creating': '创建账户中...',
    'register.success': '注册成功！请登录您的账户。',
    'register.failed': '注册失败，请稍后再试',
    'register.haveAccount': '已有账户？',
    'register.signIn': '立即登录',
    
    // Validation messages
    'validation.usernameTooShort': '用户名必须至少3个字符',
    'validation.usernameTooLong': '用户名不能超过20个字符',
    'validation.usernameInvalid': '用户名只能包含字母、数字和下划线',
    'validation.passwordTooShort': '密码必须至少6个字符',
    'validation.passwordTooLong': '密码不能超过50个字符',
    'validation.passwordMismatch': '密码不匹配',
    
    // Agent page
    'agent.title': 'AI Agent 对话',
    'agent.subtitle': '与AI助手对话，创建智能Agent',
    'agent.chatTab': '对话模块',
    'agent.editorTab': 'Agent编辑器',
    'agent.chatTitle': 'AI 对话',
    'agent.chatDescription': '与AI助手进行对话，描述您的需求',
    'agent.inputPlaceholder': '输入您的消息...',
    'agent.quickActions': '快捷操作',
    'agent.quickActionsDesc': '常用功能快速访问',
    'agent.viewAgentStatus': '查看Agent状态',
    'agent.saveChat': '保存对话',
    'agent.copyChat': '复制对话',
    'agent.exportChat': '导出对话',
    'agent.editorTitle': 'Agent 编辑器',
    'agent.editorDescription': '查看和编辑Agent的处理流程',
    'agent.intentRecognition': '意图识别',
    'agent.intentDescription': '分析用户意图和需求',
    'agent.dataIntegration': '数据整合',
    'agent.dataDescription': '收集和整合相关数据',
    'agent.executionTools': '执行工具',
    'agent.toolsDescription': '选择合适的工具执行任务',
    'agent.stepContent': '步骤内容...',
    'agent.backToChat': '返回对话',
    'agent.executeAgent': '执行Agent',
    'agent.models': 'AI 模型',
    'agent.modelsDesc': '可用的AI模型列表',
    'agent.modelProvider': '提供商',
    'agent.modelVersion': '版本',
    'agent.modelCapabilities': '能力',
    'agent.modelMaxTokens': '最大令牌数',
    'agent.modelActive': '活跃',
    'agent.modelInactive': '非活跃',
    'agent.refreshModels': '刷新模型',
    'agent.loadingModels': '加载模型中...',
    'agent.noModels': '暂无可用模型',
    'agent.selectModel': '选择模型',
    
    // Dashboard page
    'dashboard.title': '仪表板',
    'dashboard.welcome': '欢迎回来',
    'dashboard.overview': '概览',
    'dashboard.overviewDesc': '您的AI助手和平台使用情况',
    'dashboard.stats': '统计',
    'dashboard.totalAgents': '总助手',
    'dashboard.activeAgents': '活跃助手',
    'dashboard.totalUsage': '总使用量',
    'dashboard.recentActivity': '最近活动',
    'dashboard.recentActivityDesc': '您的助手最近活动',
    'dashboard.quickActions': '快捷操作',
    'dashboard.quickActionsDesc': '常用任务和快捷方式',
    'dashboard.createAgent': '创建助手',
    'dashboard.viewAll': '查看全部',
    'dashboard.noActivity': '没有最近活动',
    'dashboard.noActivityDesc': '您的助手活动将显示在这里',
    
    // Agent List
    'agent.myAgents': '我的助手',
    'agent.manageAgents': '管理和监控您的AI助手',
    'agent.createNew': '创建新助手',
    'agent.createFirst': '创建您的第一个助手',
    'agent.noAgents': '暂无助手',
    'agent.noAgentsDesc': '创建您的第一个AI助手以开始',
    'agent.chat': '对话',
    'agent.edit': '编辑',
    'agent.delete': '删除',
    'agent.activate': '激活',
    'agent.deactivate': '停用',
    'agent.usageCount': '使用次数',
    'agent.lastUsed': '最后使用',
    'agent.status.active': '活跃',
    'agent.status.inactive': '非活跃',
    'agent.status.draft': '草稿',
    'agent.status.error': '错误',
    'agent.type.conversation': '对话',
    'agent.type.fileConversion': '文件转换',
    'agent.type.imageGeneration': '图像生成',
    'agent.type.codeAssistant': '代码助手',
    
    // Agent Detail Page
    'agentDetail.backToDashboard': '返回仪表板',
    'agentDetail.createNewAgent': '创建新助手',
    'agentDetail.agentDetails': '助手详情',
    'agentDetail.configureNewAgent': '配置您的AI助手',
    'agentDetail.editAgentConfig': '编辑助手配置和设置',
    'agentDetail.save': '保存',
    'agentDetail.saving': '保存中...',
    'agentDetail.deactivate': '停用',
    'agentDetail.activate': '激活',
    'agentDetail.delete': '删除',
    'agentDetail.deleteConfirm': '您确定要删除 "{name}"?',
    'agentDetail.created': '已创建',
    'agentDetail.uses': '使用',
    'agentDetail.lastUsed': '最后使用',
    'agentDetail.basicInfo': '基本信息',
    'agentDetail.modelPrompt': '模型 & 提示',
    'agentDetail.advanced': '高级',
    'agentDetail.preview': '预览',
    'agentDetail.basicInformation': '基本信息',
    'agentDetail.basicInfoDesc': '配置AI助手的基本详情',
    'agentDetail.name': '名称',
    'agentDetail.namePlaceholder': '输入助手名称',
    'agentDetail.type': '类型',
    'agentDetail.selectAgentType': '选择助手类型',
    'agentDetail.description': '描述',
    'agentDetail.descriptionPlaceholder': '描述此助手的作用...',
    'agentDetail.tags': '标签',
    'agentDetail.addTag': '添加标签',
    'agentDetail.add': '添加',
    'agentDetail.modelConfiguration': '模型配置',
    'agentDetail.modelConfigDesc': '选择AI模型并配置助手的提示',
    'agentDetail.aiModel': 'AI模型',
    'agentDetail.selectModel': '选择AI模型',
    'agentDetail.systemPrompt': '系统提示',
    'agentDetail.promptPlaceholder': '输入定义助手行为的系统提示...',
    'agentDetail.promptHelp': '此提示将用于指导AI模型如何行为和响应。',
    'agentDetail.advancedSettings': '高级设置',
    'agentDetail.advancedDesc': '配置助手的高级选项',
    'agentDetail.status': '状态',
    'agentDetail.selectStatus': '选择状态',
    'agentDetail.configOptions': '配置选项',
    'agentDetail.configOptionsDesc': '高级配置选项将在未来更新中提供。',
    'agentDetail.agentPreview': '助手预览',
    'agentDetail.previewDesc': '预览助手对用户的显示效果',
    'agentDetail.agentName': '助手名称',
    'agentDetail.agentDescription': '助手描述',
    'agentDetail.model': '模型',
    'agentDetail.fillRequiredFields': '请填写所有必填字段',
    'agentDetail.agentCreated': '助手创建成功',
    'agentDetail.agentUpdated': '助手更新成功',
    'agentDetail.agentDeleted': '助手删除成功',
    'agentDetail.agentNotFound': '助手未找到',
    'agentDetail.failedLoadData': '加载助手数据失败',
    'agentDetail.failedSave': '保存助手失败',
    'agentDetail.failedDelete': '删除助手失败',
    'agentDetail.failedUpdateStatus': '更新助手状态失败',
    'agentDetail.agentActivated': '助手已激活',
    'agentDetail.agentDeactivated': '助手已停用',
    
    // Marketplace
    'marketplace.title': 'AI 工具市场',
    'marketplace.subtitle': '发现和探索强大的 AI 工具来增强您的工作流程',
    'marketplace.searchPlaceholder': '搜索 AI 工具...',
    'marketplace.featured': '精选',
    'marketplace.by': '开发者',
    'marketplace.viewDetails': '查看详情',
    'marketplace.noResults': '未找到工具',
    'marketplace.noResultsDesc': '请尝试调整搜索或筛选条件',
    'marketplace.backToMarketplace': '返回市场',
    'marketplace.toolNotFound': '工具未找到',
    'marketplace.getStarted': '开始使用',
    'marketplace.favorite': '收藏',
    'marketplace.share': '分享',
    'marketplace.reviews': '评价',
    'marketplace.mostPopular': '最受欢迎',
    'marketplace.choosePlan': '选择方案',
    'marketplace.installation': '安装',
    'marketplace.setup': '设置',
    'marketplace.usage': '使用',
    'marketplace.visitWebsite': '访问网站',
    'marketplace.contact': '联系',
    'marketplace.reply': '回复',
    'marketplace.overview': '概览',
    'marketplace.requirements': '系统要求',
    'marketplace.os': '操作系统',
    'marketplace.memory': '内存',
    'marketplace.storage': '存储',
    'marketplace.internet': '网络',
    'marketplace.details': '工具详情',
    'marketplace.version': '版本',
    'marketplace.lastUpdated': '最后更新',
    'marketplace.category': '分类',
    'marketplace.developer': '开发者',
    'marketplace.sort.featured': '精选',
    'marketplace.sort.rating': '评分最高',
    'marketplace.sort.priceLow': '价格：从低到高',
    'marketplace.sort.priceHigh': '价格：从高到低',
    'marketplace.sort.reviews': '评价最多',
    'marketplace.tabs.overview': '概览',
    'marketplace.tabs.pricing': '定价',
    'marketplace.tabs.instructions': '使用说明',
    'marketplace.tabs.developer': '开发者',
    'marketplace.tabs.reviews': '评价',
    
    // Tool names and categories
    'tools.smartCustomerService': '智能客服助手',
    'tools.financialAnalysis': '财务智能分析',
    'tools.hrManagement': '人力资源智能管理',
    'tools.supplyChain': '供应链优化系统',
    'tools.salesAssistant': '销售智能助手',
    'tools.marketAnalysis': '市场智能分析',
    'tools.productionOptimization': '生产流程优化',
    'tools.complianceMonitoring': '合规智能监控',
    'tools.decisionPlatform': '决策智能平台',
    'tools.projectManagement': '项目管理智能助手',
    'tools.knowledgeManagement': '知识管理智能系统',
    'tools.trainingPlatform': '培训智能平台',
    'tools.marketingAutomation': '智能自动营销触达',
    
    'category.customerService': '客户服务',
    'category.financialManagement': '财务管理',
    'category.humanResources': '人力资源',
    'category.supplyChain': '供应链',
    'category.salesManagement': '销售管理',
    'category.marketAnalysis': '市场分析',
    'category.productionManagement': '生产管理',
    'category.complianceManagement': '合规管理',
    'category.decisionSupport': '决策支持',
    'category.projectManagement': '项目管理',
    'category.knowledgeManagement': '知识管理',
    'category.trainingDevelopment': '培训发展',
    'category.marketingAutomation': '营销自动化',
    
    // Tool descriptions
    'tools.smartCustomerService.desc': '24/7智能客服系统，自动处理客户咨询，提升客户满意度',
    'tools.financialAnalysis.desc': 'AI驱动的财务数据分析工具，自动生成财务报表和预测分析',
    'tools.hrManagement.desc': '全流程HR管理平台，从招聘到绩效评估的AI智能化解决方案',
    'tools.supplyChain.desc': '智能供应链管理，实时监控库存，优化物流配送效率',
    'tools.salesAssistant.desc': 'AI销售助手，智能线索管理，提升销售转化率和客户关系',
    'tools.marketAnalysis.desc': '实时市场监控和分析，竞品跟踪，市场趋势预测',
    'tools.productionOptimization.desc': '智能制造解决方案，提升生产效率，降低运营成本',
    'tools.complianceMonitoring.desc': '自动化合规检查，风险预警，确保企业合规运营',
    'tools.decisionPlatform.desc': '基于大数据的智能决策支持系统，提供商业洞察和建议',
    'tools.projectManagement.desc': 'AI驱动的项目管理工具，自动任务分配，进度监控和资源优化',
    'tools.knowledgeManagement.desc': '企业知识库智能管理，文档自动分类，知识检索和分享',
    'tools.trainingPlatform.desc': '个性化员工培训系统，AI课程推荐，学习效果评估',
    'tools.marketingAutomation.desc': '全渠道智能营销自动化平台，精准客户触达，提升转化率',
    
    // Tool tags
    'tools.tags.customerAutomation': '客服自动化',
    'tools.tags.multilingual': '多语言支持',
    'tools.tags.intelligentRouting': '智能分流',
    'tools.tags.financialAnalysis': '财务分析',
    'tools.tags.predictiveModeling': '预测建模',
    'tools.tags.riskControl': '风险控制',
    'tools.tags.recruitmentManagement': '招聘管理',
    'tools.tags.performanceEvaluation': '绩效评估',
    'tools.tags.employeeTraining': '员工培训',
    'tools.tags.inventoryManagement': '库存管理',
    'tools.tags.logisticsOptimization': '物流优化',
    'tools.tags.demandForecasting': '需求预测',
    'tools.tags.leadManagement': '线索管理',
    'tools.tags.customerRelationships': '客户关系',
    'tools.tags.salesForecasting': '销售预测',
    'tools.tags.marketMonitoring': '市场监控',
    'tools.tags.competitorAnalysis': '竞品分析',
    'tools.tags.trendPrediction': '趋势预测',
    'tools.tags.productionOptimization': '生产优化',
    'tools.tags.qualityControl': '质量控制',
    'tools.tags.equipmentMaintenance': '设备维护',
    'tools.tags.complianceChecking': '合规检查',
    'tools.tags.riskWarning': '风险预警',
    'tools.tags.auditTracking': '审计追踪',
    'tools.tags.dataAnalysis': '数据分析',
    'tools.tags.businessIntelligence': '商业智能',
    'tools.tags.decisionSupport': '决策支持',
    'tools.tags.taskManagement': '任务管理',
    'tools.tags.progressMonitoring': '进度监控',
    'tools.tags.resourceOptimization': '资源优化',
    'tools.tags.knowledgeBase': '知识库',
    'tools.tags.documentManagement': '文档管理',
    'tools.tags.intelligentRetrieval': '智能检索',
    'tools.tags.onlineTraining': '在线培训',
    'tools.tags.personalizedLearning': '个性化学习',
    'tools.tags.effectivenessEvaluation': '效果评估',
    'tools.tags.marketingAutomation': '营销自动化',
    'tools.tags.customerReach': '客户触达',
    'tools.tags.conversionOptimization': '转化优化',
  }
};

// Get browser language
const getBrowserLanguage = (): Language => {
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('zh')) {
    return 'zh';
  }
  return 'en';
};

// Get stored language or default to browser language
const getStoredLanguage = (): Language => {
  const stored = localStorage.getItem('language');
  if (stored === 'en' || stored === 'zh') {
    return stored;
  }
  return getBrowserLanguage();
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getStoredLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  useEffect(() => {
    // Update document language attribute
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 